import React, { useState } from 'react';
import { Button, Form, Alert, Container, Row, Col, Card } from 'react-bootstrap';
import useUser from '../hooks/useUser';
import { clearAllAuthState } from '../utils/authTestingUtils';

const AutoLoginTest = () => {
  const { isAuthenticated, user, logout } = useUser();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const reportPaymentProofRequired = (testName) => {
    setLoading(false);
    setError(null);
    setResult({
      message: `${testName} now requires Stripe payment or setup intent proof. Use the checkout flow to exercise post-purchase auto-login.`,
      autoLoginSuccess: false,
    });
  };

  const testAutoLogin = () => reportPaymentProofRequired('Basic auto-login test');

  const testFrontendAutoLogin = () => reportPaymentProofRequired('Frontend auto-login test');

  const testSeamlessAutoLogin = () => reportPaymentProofRequired('Seamless auto-login test');

  const testLogout = async () => {
    console.log('[AutoLoginTest] Testing logout...');
    setLoading(true);
    setError(null);
    
    try {
      // First log the auth state before logout
      console.log('[AutoLoginTest] Auth state before logout:', {
        isAuthenticated,
        user: user?.email,
        directAuthUser: localStorage.getItem('directAuthUser'),
        autoLoginActive: sessionStorage.getItem('autoLoginActive')
      });
      
      await logout();
      console.log('[AutoLoginTest] Logout completed');
      
      // Wait a moment then check if state was cleared
      setTimeout(() => {
        const authState = {
          isAuthenticated,
          user: user?.email,
          directAuthUser: localStorage.getItem('directAuthUser'),
          autoLoginActive: sessionStorage.getItem('autoLoginActive')
        };
        console.log('[AutoLoginTest] Auth state after logout:', authState);
        
        if (!authState.directAuthUser && !authState.autoLoginActive) {
          setResult({ 
            message: '✅ Logout test successful! All auth state cleared.', 
            logoutTest: true,
            authStateAfter: authState
          });
        } else {
          setError('⚠️ Logout may not have cleared all state. Check console for details.');
        }
      }, 2000);
      
    } catch (error) {
      console.error('[AutoLoginTest] Logout error:', error);
      setError('Logout failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const clearAuthState = () => {
    console.log('[AutoLoginTest] Clearing all auth state...');
    clearAllAuthState();
    window.location.reload();
  };

  const handleRedirect = () => {
    if (result?.loginUrl) {
      console.log('[AutoLoginTest] Redirecting to Auth0:', result.loginUrl);
      // Actually perform the redirect to test the full auto-login flow
      window.location.href = result.loginUrl;
    }
  };

  const testOneClickUpgradeSuccess = () => {
    // Simulate a successful one-click upgrade by navigating to the upgrade page
    // with the proper parameters and then triggering a success state
    const testParams = new URLSearchParams({
      from_starter_signup: 'true',
      name: 'Test User',
      email: 'test@example.com',
      customerId: 'cus_test123',
      paymentMethodId: 'pm_test456',
      last4: '4242',
      token: 'test_token_789',
      source: 'starter_signup',
      test_success: 'true' // Special flag to trigger success simulation
    });
    
    // Navigate to the upgrade page with test parameters
    const upgradeUrl = `/upgrade-to-pro?${testParams.toString()}`;
    console.log('[AutoLoginTest] Navigating to upgrade page for success test:', upgradeUrl);
    window.location.href = upgradeUrl;
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card>
            <Card.Header>
              <h3>Auto-Login Test</h3>
              <p className="mb-0 text-muted">
                Test the auto-login flow after purchase simulation
              </p>
            </Card.Header>
            <Card.Body>
              {/* Auth Status Display */}
              <Alert variant={isAuthenticated ? 'success' : 'secondary'} className="mb-3">
                <strong>Auth Status:</strong> {isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
                {user && (
                  <>
                    <br />
                    <strong>User:</strong> {user.email || user.name || 'Unknown'}
                  </>
                )}
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter test email"
                  />
                </Form.Group>
                
                <Button 
                  variant="primary" 
                  onClick={testAutoLogin}
                  disabled={!email || loading}
                  className="me-2 mb-2"
                >
                  {loading ? 'Testing...' : 'Test Basic Auto-Login'}
                </Button>

                <Button
                  variant="secondary"
                  onClick={testFrontendAutoLogin}
                  disabled={loading}
                  className="me-2 mb-2"
                >
                  {loading ? 'Testing...' : 'Test Frontend Flow'}
                </Button>

                <Button
                  variant="success"
                  onClick={testSeamlessAutoLogin}
                  disabled={loading}
                  className="me-2 mb-2"
                >
                  {loading ? 'Testing...' : '🚀 Test Seamless Auto-Login'}
                </Button>

                <Button
                  variant="danger"
                  onClick={testLogout}
                  disabled={!isAuthenticated}
                  className="me-2 mb-2"
                >
                  Test Logout
                </Button>

                <Button
                  variant="warning"
                  onClick={clearAuthState}
                  className="me-2 mb-2"
                >
                  Clear All Auth State
                </Button>

                <Button
                  variant="light"
                  onClick={testOneClickUpgradeSuccess}
                  className="me-2 mb-2"
                >
                  Test One-Click Upgrade Success
                </Button>
              </Form>

              {error && (
                <Alert variant="danger" className="mt-3">
                  <strong>Error:</strong> {error}
                </Alert>
              )}

              {result && (
                <Alert variant="success" className="mt-3">
                  <h5>Auto-Login Test Results:</h5>
                  <ul>
                    <li>User Created/Found: {result.userCreated ? '✅' : '❌'}</li>
                    <li>Auto-Login Success: {result.autoLoginSuccess ? '✅' : '❌'}</li>
                    {result.tokenValidation !== undefined && (
                      <li>Token Validation: {result.tokenValidation ? '✅' : '❌'}</li>
                    )}
                    {result.userId && <li>User ID: {result.userId}</li>}
                    {result.method && <li>Login Method: <strong>{result.method}</strong></li>}
                    {result.testEmail && <li>Test Email: {result.testEmail}</li>}
                  </ul>
                  
                  {result.loginUrl && !result.isSeamless && (
                    <div className="mt-3">
                      <h6>Generated Login URL:</h6>
                      <div className="bg-light p-2 rounded mb-3" style={{ fontSize: '0.9em', wordBreak: 'break-all' }}>
                        {result.loginUrl}
                      </div>
                      <Button variant="primary" onClick={handleRedirect}>
                        🔐 Test Login (May show Auth0 UI)
                      </Button>
                    </div>
                  )}
                  
                  {result.message && (
                    <div className="mt-3">
                      <Alert variant="info">
                        {result.message}
                      </Alert>
                      {result.testUrl && (
                        <div>
                          <strong>Test URL:</strong> {result.testUrl}
                        </div>
                      )}
                    </div>
                  )}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AutoLoginTest;
